import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

export interface DashboardFilters {
  policyAreaId: string | null;
  variableId: string | null;
}

interface FilterContextValue {
  filters: DashboardFilters;
  setPolicyArea: (id: string | null) => void;
  setVariable: (id: string | null) => void;
  clearFilters: () => void;
  navigateToArea: (areaId: string) => void;
  navigateToVariable: (variableId: string, policyAreaId?: string) => void;
}

const FilterContext = createContext<FilterContextValue | null>(null);

export function FilterProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<DashboardFilters>({
    policyAreaId: null,
    variableId: null,
  });

  const setPolicyArea = useCallback((id: string | null) => {
    setFilters((f) => ({ ...f, policyAreaId: id }));
  }, []);

  const setVariable = useCallback((id: string | null) => {
    setFilters((f) => ({ ...f, variableId: id }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ policyAreaId: null, variableId: null });
  }, []);

  const navigateToArea = useCallback((areaId: string) => {
    setFilters({ policyAreaId: areaId, variableId: null });
    navigate(`/areas/${areaId}`);
  }, [navigate]);

  const navigateToVariable = useCallback((variableId: string, policyAreaId?: string) => {
    setFilters({ policyAreaId: policyAreaId ?? null, variableId });
    if (policyAreaId) navigate(`/areas/${policyAreaId}?variable=${variableId}`);
  }, [navigate]);

  const value = useMemo(
    () => ({
      filters,
      setPolicyArea,
      setVariable,
      clearFilters,
      navigateToArea,
      navigateToVariable,
    }),
    [filters, setPolicyArea, setVariable, clearFilters, navigateToArea, navigateToVariable],
  );

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
}

export function useFilters() {
  const ctx = useContext(FilterContext);
  if (!ctx) throw new Error('useFilters must be used within FilterProvider');
  return ctx;
}
